
/*******************************/
const SUBSCRIPTION_REFRESH_DELAY = 150;
const EXTRA_SUBSCRIPTION_POINTS = [
  'Save 10% off your order',
  'Easily pause, swap or skip',
  'Cancel quickly anytime'
];
const SUBSCRIPTION_VISIBILITY_TARGETS = [
  '.subscription-select',
  '.shopify_subscriptions_app_block_label_children',
  '.extra-subscription-points'
];
const SUBSCRIPTION_WIDGET_ACTIVE_CLASS = 'is-plan-active';

let lastPurchaseMode = 'subscription';

function parsePriceToNumber(value) {
  if (typeof value === 'number') {
    return value;
  }

  if (!value) return NaN;

  let normalized = String(value).trim();
  if (!normalized) return NaN;

  normalized = normalized.replace(/[^0-9.,-]/g, '');

  const commaIndex = normalized.lastIndexOf(',');
  const dotIndex = normalized.lastIndexOf('.');

  if (commaIndex > -1 && dotIndex > -1) {
    if (commaIndex > dotIndex) {
      normalized = normalized.replace(/\./g, '').replace(',', '.');
    } else {
      normalized = normalized.replace(/,/g, '');
    }
  } else if (commaIndex > -1 && dotIndex === -1) {
    normalized = normalized.replace(',', '.');
  } else {
    normalized = normalized.replace(/,/g, '');
  }

  const parsed = parseFloat(normalized);
  return Number.isNaN(parsed) ? NaN : parsed;
}

function formatPerBottleValue(value) {
  if (!isFinite(value)) return '';
  const locale = document.documentElement.lang || 'en-US';
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value);
}

function ensureBottleTextNode(priceEl) {
  let bottleText = priceEl.querySelector('.bottle--text');
  if (!bottleText) {
    bottleText = document.createElement('span');
    bottleText.className = 'bottle--text';
    bottleText.textContent = 'per Bottle';
  }
  return bottleText;
}

function restoreDefaultVariantPrice(priceEl) {
  if (!priceEl) return;
  const symbol = priceEl.dataset.currencySymbol || '';
  const defaultValue = priceEl.dataset.defaultPerBottle || '';
  if (!defaultValue) return;

  const bottleText = ensureBottleTextNode(priceEl);
  priceEl.textContent = `${symbol}${defaultValue} `;
  priceEl.appendChild(bottleText);
  delete priceEl.dataset.calculatedPerBottle;
}

function applyCalculatedVariantPrice(priceEl, perBottle) {
  if (!priceEl || !isFinite(perBottle)) return;
  const symbol = priceEl.dataset.currencySymbol || '';
  const bottleText = ensureBottleTextNode(priceEl);
  const formatted = formatPerBottleValue(perBottle);

  priceEl.textContent = `${symbol}${formatted} `;
  priceEl.appendChild(bottleText);
  priceEl.dataset.calculatedPerBottle = perBottle;
}

function getVariantSelectionEl() {
  return document.querySelector('variant-selection');
}

function getCurrentVariantId() {
  const variantSelection = getVariantSelectionEl();

  if (variantSelection) {
    const variantId = variantSelection.getAttribute('variant');
    if (variantId) return variantId;
  }

  const fallbackSelect = document.querySelector('select[name="id"]');
  return fallbackSelect ? fallbackSelect.value : null;
}

function getActiveSubscriptionBlock() {
  const variantId = getCurrentVariantId();

  if (variantId) {
    const scopedBlock = document.querySelector(
      `.shopify_subscriptions_app_block[data-variant-id="${variantId}"]`
    );
    if (scopedBlock) return scopedBlock;
  }

  return document.querySelector('.shopify_subscriptions_app_block:not(.shopify_subscriptions_app_block--hidden)') ||
    document.querySelector('.shopify_subscriptions_app_block');
}

function buildSubscriptionSelect() {
  const block = getActiveSubscriptionBlock();
  if (!block) return;

  const list = block.querySelector('.shopify_subscriptions_app_block_label_children');
  if (!list) return;

  ensureExtraSubscriptionPoints(block, list);
  attachSubscriptionBlockListeners(block);

  const oldSelect = block.querySelector('.subscription-select');
  if (oldSelect) oldSelect.remove();

  const select = document.createElement('select');
  select.className = 'subscription-select';

  list.querySelectorAll('li label').forEach(label => {
    const input = label.querySelector('input[data-selling-plan-id]');
    if (!input) return;

    const option = document.createElement('option');
    option.value = input.dataset.sellingPlanId;
    option.textContent = label.textContent.trim();
    select.appendChild(option);
  });

  if (!select.children.length) return;

  list.parentNode.insertBefore(select, list);

  select.addEventListener('change', function () {
    const id = this.value;
    const targetInput = list.querySelector(`input[data-selling-plan-id="${id}"]`);
    if (targetInput) {
      targetInput.checked = true;
      targetInput.dispatchEvent(new Event('input', { bubbles: true }));
      targetInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  });
}

function ensureExtraSubscriptionPoints(block, sourceList) {
  const existing = block.querySelector('.extra-subscription-points');
  if (existing) return;

  const list = sourceList || block.querySelector('.shopify_subscriptions_app_block_label_children');
  if (!list) return;

  const extraList = document.createElement('ul');
  extraList.className = 'extra-subscription-points';

  EXTRA_SUBSCRIPTION_POINTS.forEach(text => {
    const li = document.createElement('li');
    li.textContent = text;
    extraList.appendChild(li);
  });

  list.insertAdjacentElement('afterend', extraList);
}

function syncSubscriptionSelect(block, radio) {
  const select = block.querySelector('.subscription-select');
  if (select) {
    select.value = radio.dataset.sellingPlanId || select.value;
  }
}

function setSubscriptionVisibility(block, expanded) {
  if (!block) return;

  SUBSCRIPTION_VISIBILITY_TARGETS.forEach(selector => {
    block.querySelectorAll(selector).forEach(element => {
      if (expanded) {
        element.removeAttribute('hidden');
        element.style.display = '';
      } else {
        element.setAttribute('hidden', 'hidden');
        element.style.display = 'none';
      }
    });
  });

  block.classList.toggle('subscription-collapsed', !expanded);
}

function getCheckedSubscriptionOption(block = getActiveSubscriptionBlock()) {
  if (!block) return null;
  return block.querySelector('input[data-selling-plan-id]:checked');
}

function updateSubscriptionWidgetPrice(block = getActiveSubscriptionBlock()) {
  if (!block) return;

  const planDisplay = block.querySelector('.shopify_subscriptions_purchase_option_wrapper .shopify_subscriptions_in_widget_price');
  const widgets = block.querySelectorAll('.shopify_subscriptions_in_widget_price');

  const selectedOption = getCheckedSubscriptionOption(block);
  const planId = selectedOption?.dataset.sellingPlanId || '';
  const planLabel = selectedOption?.closest('label')?.textContent.trim() || '';
  const planPrice = selectedOption?.dataset.variantPrice || selectedOption?.getAttribute('data-variant-price');

  widgets.forEach(widget => {
    if (!selectedOption) {
      widget.classList.remove(SUBSCRIPTION_WIDGET_ACTIVE_CLASS);
      widget.removeAttribute('data-plan-id');
      widget.removeAttribute('data-plan-label');
    } else {
      widget.classList.add(SUBSCRIPTION_WIDGET_ACTIVE_CLASS);
      widget.setAttribute('data-plan-id', planId);
      widget.setAttribute('data-plan-label', planLabel);
    }
  });

  if (!selectedOption) {
    console.log('[Subscription]', 'No selling plan selected for current variant.');
    updateVariantPerBottlePricing();
    return;
  }

  // if (planDisplay && planPrice) {
  //   planDisplay.textContent = planPrice;
  // }

  console.log('[Subscription]', 'Active plan updated:', { planId, planLabel, planPrice });
  updateVariantPerBottlePricing(planPrice);
}

function chooseDefaultSubscription() {
  const block = getActiveSubscriptionBlock();
  if (!block) return false;

  const container = block.querySelector('.shopify_subscriptions_app_block_label_children');
  if (!container) return false;

  const sellingRadios = Array.from(container.querySelectorAll('input[data-selling-plan-id]'));
  if (!sellingRadios.length) return false;

  const defaultRadio = sellingRadios.find(r => {
    const labelEl = r.closest('label');
    const labelText = labelEl ? labelEl.textContent : r.getAttribute('aria-label') || '';
    return /subscribe/i.test(labelText);
  }) || sellingRadios[0];

  const oneTime = block.querySelector('input[data-radio-type="one_time_purchase"]');
  if (oneTime && oneTime.checked) {
    try { oneTime.checked = false; } catch (e) {}
    oneTime.dispatchEvent(new Event('input', { bubbles: true }));
    oneTime.dispatchEvent(new Event('change', { bubbles: true }));
  }

  if (!defaultRadio.checked) {
    try { defaultRadio.checked = true; } catch (e) {}
    defaultRadio.dispatchEvent(new Event('input', { bubbles: true }));
    defaultRadio.dispatchEvent(new Event('change', { bubbles: true }));
    try { defaultRadio.click(); } catch (e) {}
  }

  syncSubscriptionSelect(block, defaultRadio);
  setSubscriptionVisibility(block, true);
  lastPurchaseMode = 'subscription';
  updateSubscriptionWidgetPrice(block);
  return true;
}

function selectOneTimePurchase(block = getActiveSubscriptionBlock()) {
  if (!block) return false;

  const oneTimeRadio = block.querySelector('input[data-radio-type="one_time_purchase"]');
  if (!oneTimeRadio) return false;

  if (!oneTimeRadio.checked) {
    try { oneTimeRadio.checked = true; } catch (e) {}
    oneTimeRadio.dispatchEvent(new Event('input', { bubbles: true }));
    oneTimeRadio.dispatchEvent(new Event('change', { bubbles: true }));
  }

  setSubscriptionVisibility(block, false);
  lastPurchaseMode = 'onetime';
  updateSubscriptionWidgetPrice(block);
  return true;
}

function applyPreferredPurchaseMode() {
  const block = getActiveSubscriptionBlock();
  if (!block) return false;

  if (lastPurchaseMode === 'onetime' && selectOneTimePurchase(block)) {
    return true;
  }

  if (chooseDefaultSubscription()) {
    return true;
  }

  return selectOneTimePurchase(block);
}

function initSubscriptionToggle() {
  const block = getActiveSubscriptionBlock();
  if (!block) return;
  ensureExtraSubscriptionPoints(block);
  attachSubscriptionBlockListeners(block);

  const subBlock = block
    .querySelector('.shopify_subscriptions_app_block_label .shopify_subscriptions_purchase_option_wrapper')
    ?.closest('.shopify_subscriptions_app_block_label');

  if (!subBlock || subBlock.querySelector('.subscription-group-toggle')) return;

  const sellingRadio = subBlock.querySelector('input[data-selling-plan-id]');
  if (!sellingRadio) return;

  const oneTimeRadio = block.querySelector('input[data-radio-type="one_time_purchase"]');
  const radiosName = (oneTimeRadio && oneTimeRadio.name) || sellingRadio.name;

  const toggleWrapper = document.createElement('div');
  toggleWrapper.className = 'subscription-group-toggle';

  const groupRadio = document.createElement('input');
  groupRadio.type = 'radio';
  groupRadio.name = radiosName;
  groupRadio.dataset.radioType = 'selling_plan_group';
  groupRadio.value = sellingRadio.dataset.sellingPlanGroupId || sellingRadio.dataset.sellingPlanId;

  const label = document.createElement('label');
  label.style.cursor = 'pointer';
  label.style.fontWeight = '500';
  label.prepend(groupRadio, ' Subscribe and save');

  toggleWrapper.appendChild(label);

  const insertBeforeEl =
    subBlock.querySelector('.shopify_subscriptions_purchase_option_wrapper') ||
    subBlock.firstChild;

  subBlock.insertBefore(toggleWrapper, insertBeforeEl);

  function activateSubscriptionGroup() {
    const sellingRadios = subBlock.querySelectorAll('input[data-selling-plan-id]');
    if (!sellingRadios.length) return;

    const target = sellingRadios[0];

    if (oneTimeRadio && oneTimeRadio.checked) {
      oneTimeRadio.checked = false;
      oneTimeRadio.dispatchEvent(new Event('change', { bubbles: true }));
    }

    target.checked = true;
    target.dispatchEvent(new Event('input', { bubbles: true }));
    target.dispatchEvent(new Event('change', { bubbles: true }));

    const select = block.querySelector('.subscription-select');
    if (select) {
      select.value = target.dataset.sellingPlanId;
      select.dispatchEvent(new Event('change', { bubbles: true }));
    }

    toggleWrapper.classList.add('active');
      setSubscriptionVisibility(block, true);
      lastPurchaseMode = 'subscription';
    updateSubscriptionWidgetPrice(block);
  }

  groupRadio.addEventListener('change', function () {
    if (this.checked) activateSubscriptionGroup();
  });

  groupRadio.checked = true;
  activateSubscriptionGroup();
}

function refreshSubscriptionUI() {
  buildSubscriptionSelect();
  initSubscriptionToggle();
  const applied = applyPreferredPurchaseMode();
  updateSubscriptionWidgetPrice();
  return applied;
}

function ensureSubscriptionUIReady() {
  if (refreshSubscriptionUI()) return;

  const observer = new MutationObserver((mutations, obs) => {
    if (refreshSubscriptionUI()) {
      obs.disconnect();
    }
  });

  observer.observe(document.body, { childList: true, subtree: true });
  setTimeout(() => observer.disconnect(), 5000);
}

function attachVariantChangeListeners() {
  const variantSelection = getVariantSelectionEl();
  if (variantSelection) {
    variantSelection.addEventListener('variant-change', () => {
      setTimeout(refreshSubscriptionUI, SUBSCRIPTION_REFRESH_DELAY);
    });
  }

  const fallbackSelect = document.querySelector('select[name="id"]');
  if (fallbackSelect) {
    fallbackSelect.addEventListener('change', () => {
      setTimeout(refreshSubscriptionUI, SUBSCRIPTION_REFRESH_DELAY);
    });
  }

  document.addEventListener('variant:change', () => {
    setTimeout(refreshSubscriptionUI, SUBSCRIPTION_REFRESH_DELAY);
  });
}

function handleOneTimeToggle(event) {
  if (!event.target.matches('input[data-radio-type="one_time_purchase"]')) return;

  const block = event.target.closest('.shopify_subscriptions_app_block');
  if (!block) return;

  const toggle = block.querySelector('.subscription-group-toggle');
  if (toggle) toggle.classList.remove('active');

  lastPurchaseMode = 'onetime';
  setSubscriptionVisibility(block, false);
  updateSubscriptionWidgetPrice(block);
}

function getPackSwatchOptions() {
  return document.querySelectorAll('.swatch__options .swatch__option[data-pack-quantity]');
}

function getSelectedPackSwatchOption() {
  const options = getPackSwatchOptions();
  for (const option of options) {
    const input = option.querySelector('input[data-variant-option-value]');
    if (input && input.checked) {
      return option;
    }
  }
  return null;
}

function updateVariantPerBottlePricing(planPrice) {
  const planPriceValue = parsePriceToNumber(planPrice);
  const packSwatchOptions = getPackSwatchOptions();
  if (!packSwatchOptions.length) return;

  const selectedOption = getSelectedPackSwatchOption();
  const selectedBasePrice = selectedOption ? parseFloat(selectedOption.dataset.variantPrice) : NaN;
  const hasValidPlanPrice = Number.isFinite(planPriceValue) && planPriceValue > 0;
  const hasValidSelectedBase = Number.isFinite(selectedBasePrice) && selectedBasePrice > 0;
  const discountMultiplier = hasValidPlanPrice && hasValidSelectedBase
    ? planPriceValue / selectedBasePrice
    : null;

  packSwatchOptions.forEach(option => {
    const priceEl = option.querySelector('.variant-price');
    const packQuantity = parseFloat(option.dataset.packQuantity);
    const baseVariantPrice = parseFloat(option.dataset.variantPrice);

    if (!priceEl || !packQuantity || packQuantity <= 0) {
      restoreDefaultVariantPrice(priceEl);
      return;
    }

    if (!discountMultiplier || !Number.isFinite(baseVariantPrice) || baseVariantPrice <= 0) {
      restoreDefaultVariantPrice(priceEl);
      return;
    }

    const discountedPackPrice = baseVariantPrice * discountMultiplier;
    const perBottle = discountedPackPrice / packQuantity;
    if (!isFinite(perBottle) || perBottle <= 0) {
      restoreDefaultVariantPrice(priceEl);
      return;
    }

    applyCalculatedVariantPrice(priceEl, perBottle);
  });
}

document.addEventListener('DOMContentLoaded', () => {
  setTimeout(ensureSubscriptionUIReady, SUBSCRIPTION_REFRESH_DELAY);
  attachVariantChangeListeners();

  document.body.addEventListener('change', handleOneTimeToggle);
  updateVariantPerBottlePricing();
});

/***********************************************/

function attachSubscriptionBlockListeners(block) {
  if (!block || block.dataset.subscriptionListenersAttached === 'true') return;

  block.addEventListener('change', event => {
    if (event.target.matches('input[data-selling-plan-id]')) {
      lastPurchaseMode = 'subscription';
      setSubscriptionVisibility(block, true);
      updateSubscriptionWidgetPrice(block);
    }
  });

  block.dataset.subscriptionListenersAttached = 'true';
}

/**********************08-12************************* */

setTimeout(function () {
  const parent = document.querySelector(".product__information--inner");
  if (!parent) return;

  const swatchOptions = parent.querySelectorAll(".swatch__option");
  const variantHiddenInput = parent.querySelector("#variant__id");
  const productIdInput = parent.querySelector('input[name="product-id"]');

  swatchOptions.forEach(option => {
    // Use 'click' with preventDefault to avoid double-firing
    option.addEventListener("click", function (e) {
      e.preventDefault(); // Prevent default to control flow
      e.stopPropagation(); // Stop other handlers from interfering

      const variantId = this.getAttribute("data-variant-id");
      console.log("Swatch clicked →", variantId);

      // 1. Update hidden inputs
      if (variantHiddenInput) {
        variantHiddenInput.value = variantId;
      }
      if (productIdInput) {
        productIdInput.value = variantId;
      }

      // 2. Mark radio button
      const radio = this.querySelector('input[type="radio"]');
      if (radio) {
        radio.checked = true;
        radio.dispatchEvent(new Event("change", { bubbles: true }));
      }

      // 3. CRITICAL: Update variant-selection (fixes subscription script)
      const variantSelection = document.querySelector('variant-selection');
      if (variantSelection) {
        variantSelection.setAttribute('variant', variantId);
        // Trigger subscription script's listener
        variantSelection.dispatchEvent(new CustomEvent('variant-change', { 
          bubbles: true, 
          detail: { variant: { id: variantId } }
        }));
      }

      // 4. Trigger hidden input change
      if (variantHiddenInput) {
        variantHiddenInput.dispatchEvent(new Event("change", { bubbles: true }));
      }

      // 5. Legacy theme events
      document.dispatchEvent(new CustomEvent("variant:changed", {
        detail: { variant: { id: variantId } }
      }));

      // 6. Appstle subscription events
      document.dispatchEvent(new CustomEvent("appstle:subscription-product:updated", {
        detail: { variantId: variantId }
      }));

      // 7. Appstle global reload (if available)
      if (window.AppstleSubscriptions) {
        try {
          window.AppstleSubscriptions.reloadProductPage();
        } catch (e) {}
      }

      console.log("✅ Full variant update →", variantId);
    }, { once: false }); // Allow multiple clicks
  });

  console.log("✅ Swatch handlers attached");
}, 100);